import { Autocomplete, Button, IconButton, Input } from '@fullscreendigital/carrefour-dex-fe'
import { useMutation, useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { useLocation, useNavigate } from 'react-router-dom'
import { URLS } from 'components/shop-reception/mobile/constants'
import { useEffect, useMemo, useState } from 'react'
import ImageUpload from 'components/shop-reception/mobile/Common/ImageUpload/ImageUpload'
import { useFormik } from 'formik'
import { toBoolean } from 'utils/helpers'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import AlertModal from 'components/shop-reception/mobile/Common/AlertModal/AlertModal'
import { getInitialValues, markOptions, reasonOptions, toSupportHubPayload } from './helpers'
import { validateScanCode } from '../../../../helpers'
import { ManageSearch as ManageSearchIcon } from '@mui/icons-material'
import { FlexCenter } from '../../../../../Common/FlexCenter'
import { useOfflineElementTool } from 'components/core-ui/Offline'

const ExpectedTrucksStep3 = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showNotification } = useNotification()

  const truckId = location.state.item
  const [supportCode, setSupportCode] = useState('')
  const [isSupportImageUploaded, setIsSupportImageUploaded] = useState(false)
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [supportType, setSupportType] = useState(null)
  const [reason, setReason] = useState(null)
  const [supportCounter, setSupportCounter] = useState(0)
  const [totalSupportNumber, setTotalSupportNumber] = useState(null)

  const { offlineData, deleteOfflineData, saveOfflineData } = useOfflineElementTool({
    dataType: 'EXPECTED_TRUCKS_STEP_3',
    url: URLS.AUTH_EXPECTED_TRUCKS_STEP3,
    elementId: truckId
  })

  const { data: hubName, isLoading: isLoadingHubName } = useQuery(
    ['hub-name'],
    async () => {
      const response = await makeCoreGetRequest(`/shop-reception-mobile/home/hub-name`)
      return response || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: counter, isLoading: isCounterLoading } = useQuery(
    ['counter'],
    async () => {
      const response = await makeCoreGetRequest(
        `/shop-reception-mobile/support/hub/counter/${truckId}`
      )

      setSupportCounter(response.counter)
      return response
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: typeOptions, isLoading: isTypeOptionsLoading } = useQuery(
    ['type-options'],
    async () => {
      const response = await makeCoreGetRequest(
        `/shop-reception-mobile/support/shop/principal/type`
      )

      return response.map(type => ({ label: type.name, value: type.id }))
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: hubTruck, isLoading: isHubTruckLoading } = useQuery(
    ['type-options'],
    async () => {
      const response = await makeCoreGetRequest(`/shop-reception-mobile/truck/hub/${truckId}`)

      return response || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const summaryQuery = useQuery(
    ['summary'],
    async () => {
      return await makeCoreGetRequest(`/shop-reception-mobile/support/hub/summary/${truckId}`)
    },

    {
      enabled: false,
      onSuccess: data => {
        setSupportCounter(data.counter)
        setTotalSupportNumber(data.total)
        data.counter !== data.total
          ? setIsAlertOpen(true)
          : navigate(URLS.AUTH_EXPECTED_TRUCKS_STEP5, {
              state: { truckId }
            })
      },
      onError: res => {
        showNotification({
          message: `A aparut o eroare la obtinerea counter-ului., ${formatErrorMessage(res)}`,
          type: 'error'
        })
      }
    }
  )

  const scan = async values => {
    const response = await makeCorePostRequest(
      `/shop-reception-mobile/support/hub/scan-by-code/${truckId}`,
      { supportCode: values }
    )
    if (response.errorMessage) {
      showNotification({
        message: `A aparut o eroare la cautarea codului. ${response.errorMessage}`,
        type: 'error'
      })
      return
    }

    return response
  }

  const {
    isLoading: isLoadingScan,
    mutate: scanCode,
    data: scanCodeData,
    reset: resetScanData
  } = useMutation(scan, {
    onError: res => {
      showNotification({
        message: `A aparut o eroare ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const saveSupport = async values => {
    const resp = await makeCorePostRequest(
      `/shop-reception-mobile/support/hub/save/${truckId}`,
      toSupportHubPayload(values)
    )

    const localCounter = resp.counter
    if (localCounter > 0) {
      setSupportCounter(localCounter)
    }
  }

  const { isLoading, mutate } = useMutation(saveSupport, {
    onSuccess: async () => {
      showNotification({
        message: 'Confirmat',
        type: 'success'
      })
      resetScanData()
      setSupportCode(null)
      await deleteOfflineData()
      if (document.getElementById('scan-input')) {
        document.getElementById('scan-input').focus()
      }
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const initialValues = useMemo(
    () => getInitialValues(scanCodeData, offlineData?.data),
    [scanCodeData, offlineData?.data]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    onSubmit: async (values: any) => {
      mutate(values)
    }
  })

  useEffect(() => {
    ;(async () => {
      if (offlineData?.data?.offlineSupportCode) {
        const { offlineSupportCode, offlineIsSupportImageUploaded } = offlineData.data

        // support code search
        setSupportCode(offlineSupportCode)
        if (validateScanCode(showNotification, supportCode, 'suport')) {
          scanCode(offlineSupportCode)
        }

        // support image uploaded
        setIsSupportImageUploaded(offlineIsSupportImageUploaded)
      }
    })()
  }, [offlineData, formik.values?.supportCode])

  useEffect(() => {
    if (typeOptions && formik.values.supportTypeId) {
      const supportTypeOption = typeOptions.find(
        option => formik.values.supportTypeId === option.value
      )
      setSupportType(supportTypeOption)
    }
  }, [typeOptions, formik.values.supportTypeId])

  useEffect(() => {
    if (formik.values.reason) {
      const reasonOption = reasonOptions.find(option => formik.values.reason === option.value)
      setReason(reasonOption)
    }
  }, [typeOptions, formik.values.reason])

  if (
    isLoadingHubName ||
    isCounterLoading ||
    isHubTruckLoading ||
    isTypeOptionsLoading ||
    isLoadingScan ||
    isLoading
  ) {
    return (
      <div style={{ display: 'flex' }}>
        <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
      </div>
    )
  }

  return (
    <Grid container spacing={2} justifyContent={'center'}>
      <Grid xs={12} item>
        <Typography
          component="h2"
          variant="medium"
          style={{ textAlign: 'center', textTransform: 'uppercase' }}
          pt={2}
        >
          {hubName.value}
        </Typography>
      </Grid>

      <Grid xs={10} item>
        <FlexCenter>
          <Input
            type="text"
            label="Cod suport"
            variant="standard"
            width="100%"
            autoFocus
            value={supportCode}
            onChange={async e => {
              const target = e.target as HTMLInputElement
              setSupportCode(target.value)
              await saveOfflineData([{ field: 'offlineSupportCode', value: target.value }])
            }}
            onKeyUp={event => {
              const target = event.target as HTMLInputElement
              if (event.which == 0) {
                if (target.value && validateScanCode(showNotification, supportCode, 'suport')) {
                  scanCode(target.value)
                  setSupportCode('')
                }
              }
            }}
            id="scan-input"
          />
          <IconButton
            onClick={() => {
              if (validateScanCode(showNotification, supportCode, 'suport')) {
                scanCode(supportCode)
              }
            }}
          >
            <ManageSearchIcon />
          </IconButton>
        </FlexCenter>
      </Grid>

      <Grid item xs={10}>
        <Divider />
      </Grid>

      {scanCodeData && (
        <>
          <Grid xs={10} item>
            <Typography component="p" variant="medium">
              Numar suport {scanCodeData?.supportNumber}
            </Typography>
          </Grid>

          <Grid xs={10} item>
            <Autocomplete
              variant="outlined"
              label="Tip ambalaj"
              disableClearable
              options={typeOptions}
              onChange={async (event, option) => {
                formik.setFieldValue('supportTypeId', option.value)
                await saveOfflineData([{ field: 'offlineSupportTypeId', value: option.value }])
              }}
              value={supportType}
            />
          </Grid>

          <Grid xs={10} item>
            <Autocomplete
              variant="outlined"
              label="Calitativ"
              disableClearable
              options={[
                { label: 'Da', value: 'true' },
                { label: 'Nu', value: 'false' }
              ]}
              onChange={async (event, option) => {
                formik.setFieldValue('defect', !toBoolean(option.value))
                await saveOfflineData([{ field: 'offlineDefect', value: !toBoolean(option.value) }])
              }}
              value={
                formik.values.defect
                  ? { label: 'Nu', value: 'false' }
                  : { label: 'Da', value: 'true' }
              }
            />
          </Grid>

          {formik.values.defect && (
            <>
              <Grid xs={10} item>
                <Autocomplete
                  variant="outlined"
                  label="Motiv"
                  disableClearable
                  options={reasonOptions}
                  onChange={async (event, option) => {
                    formik.setFieldValue('reason', option.value)
                    await saveOfflineData([{ field: 'offlineReason', value: option.value }])
                  }}
                  value={reason}
                />
              </Grid>
              <Grid xs={10} item>
                <Autocomplete
                  variant="outlined"
                  label="Marca"
                  disableClearable
                  options={markOptions}
                  onChange={async (event, option) => {
                    formik.setFieldValue('nonCompliantReasonList', option)
                    await saveOfflineData([
                      { field: 'offlineNonCompliantReasonList', value: option }
                    ])
                  }}
                  value={formik.values.nonCompliantReasonList}
                  multiple
                />
              </Grid>

              <Grid xs={10} item>
                <ImageUpload
                  uploadUrl={`/shop-reception-mobile/support/shop/upload/${scanCodeData?.supportId}`}
                  onSuccess={async () => {
                    showNotification({
                      message: 'Imaginea a fost incarcata cu succes.',
                      type: 'success'
                    })
                    setIsSupportImageUploaded(true)
                    await saveOfflineData([{ field: 'offlineIsSupportImageUploaded', value: true }])
                  }}
                  onError={() =>
                    showNotification({
                      message: 'A aparut o eroare la incarcarea imaginii.',
                      type: 'error'
                    })
                  }
                />
              </Grid>
            </>
          )}

          <Grid xs={10} item>
            <Button
              variant="contained"
              label="Alte ambalaje"
              onClick={() => {
                navigate(URLS.AUTH_EXPECTED_TRUCKS_STEP4, {
                  state: { supportId: scanCodeData?.supportId, truckId: truckId }
                })
              }}
              style={{ width: '100%' }}
            />
          </Grid>

          <Grid xs={10} item>
            <Button
              variant="contained"
              label="Confirm tip suport"
              onClick={() => {
                formik.handleSubmit()
              }}
              style={{ width: '100%' }}
              disabled={formik.values.defect && isSupportImageUploaded == false}
            />
          </Grid>
        </>
      )}

      <Grid xs={10} item>
        <FormInfoItems
          items={[
            { label: 'Numar camion:', name: counter?.truckNumber },
            { label: 'Numar suporti scanati:', name: `${supportCounter}` }
          ]}
        />
      </Grid>

      {!scanCodeData && (
        <Grid xs={10} item>
          <Button
            variant="contained"
            label="Finalizeaza"
            onClick={() => summaryQuery.refetch()}
            style={{ width: '100%' }}
          />
        </Grid>
      )}

      <AlertModal
        open={isAlertOpen}
        handleClose={() => setIsAlertOpen(false)}
        title={'Atentie'}
        description={`Ati scanat ${supportCounter} suporti din ${totalSupportNumber} asteptati.`}
        buttons={[
          {
            label: 'Mai verific',
            onClick: () => {
              setIsAlertOpen(false)
              setTimeout(() => {
                document.getElementById('scan-input').focus()
              }, 0)
            },
            color: 'primary'
          },
          {
            label: 'Am terminat',
            onClick: () =>
              navigate(URLS.AUTH_EXPECTED_TRUCKS_STEP5, {
                state: { truckId }
              }),
            color: 'primary'
          }
        ]}
      />
    </Grid>
  )
}

export default ExpectedTrucksStep3
